// eslint-disable-next-line no-restricted-imports
import { LDClient, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

import { isLocal, isStaging } from '../config';
import {
  FLAG_TRACKING_BLACK_LIST,
  FlagOverrides,
  LDFlagValueTypes,
  LDFlags,
} from '../constants/flags';
import { RudderEvent, rudderanalytics } from '../services/analytics/rudderstack';

// For reflect
const REFLECT_KEY = 'reflectFlags';
const reflectFlags = JSON.parse(localStorage.getItem(REFLECT_KEY) as string);

// Imports ../flagOverrides.ts but uses a default {} if it doesn't exist
let flagOverrides: { default: FlagOverrides } = { default: {} };
try {
  // eslint-disable-next-line global-require, import/no-unresolved, import/extensions
  flagOverrides = require('../flagOverrides');
} catch (ex) {
  // do nothing: use default empty flags
}

const overrideLogic = (flag: LDFlags, flagValue: unknown) => {
  if (isStaging && reflectFlags?.[flag] != null) {
    return reflectFlags[flag];
  }
  if (isLocal && flagOverrides.default[flag] != null) {
    return flagOverrides.default[flag];
  }
  return flagValue;
};

const getFlagValue = <T extends LDFlags>(ldClient: LDClient | undefined, flag: T) => {
  const flagValue = ldClient?.variation(flag);
  return overrideLogic(flag, flagValue) as LDFlagValueTypes[T];
};

const trackedFlags = {} as Record<LDFlags, LDFlagValueTypes[keyof LDFlagValueTypes]>;

export const trackFlagEval = <T extends LDFlags>(flag: T, flagValue: LDFlagValueTypes[T]) => {
  if (FLAG_TRACKING_BLACK_LIST.includes(flag)) {
    return;
  }
  if (trackedFlags[flag] === flagValue) {
    return;
  }

  trackedFlags[flag] = flagValue;
  rudderanalytics.track(RudderEvent.FlowTest, {
    flag,
    flagValue,
  });
};

const evaluatedFlags = {} as Record<LDFlags, LDFlagValueTypes[keyof LDFlagValueTypes]>;

export const evaluateFlag = <T extends LDFlags>(
  ldClient: LDClient | undefined,
  flag: T,
  shouldTrack = true,
) => {
  const flagValue = getFlagValue(ldClient, flag);

  if (shouldTrack) {
    trackFlagEval(flag, flagValue);
  }
  if (evaluatedFlags[flag] === flagValue) {
    return flagValue;
  }

  window.dispatchEvent(new Event(`evaluateFlag:${flag}`));
  const isFirstEvaluation = evaluatedFlags[flag] == null;
  evaluatedFlags[flag] = flagValue;

  if (isFirstEvaluation) {
    ldClient?.on(`change:${flag}`, () => evaluateFlag(ldClient, flag));
  }
  return flagValue;
};

// a way to override flags for development until launchdarkly supports this https://github.com/launchdarkly/react-client-sdk/issues/53
// view flagOverrides.sample.ts in the root directory for more information
const useFlag = <T extends LDFlags>(
  flag: T,
  { shouldTrack = true }: { shouldTrack?: boolean } = {},
) => {
  const ldClient = useLDClient();
  const [flagValue, setFlagValue] = useState(evaluateFlag(ldClient, flag, shouldTrack));

  useEffect(() => {
    const handleEvaluateFlag = () => {
      setFlagValue(evaluateFlag(ldClient, flag, shouldTrack));
    };

    handleEvaluateFlag();
    window.addEventListener(`evaluateFlag:${flag}`, handleEvaluateFlag);
    return () => {
      window.removeEventListener(`evaluateFlag:${flag}`, handleEvaluateFlag);
    };
  }, [flag, shouldTrack]);

  return flagValue;
};

export default useFlag;
